import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { ThemeConsumer } from 'styled-components';
import { retrieveVehiclesByVin } from '../legacy/services/apiRouterService';
import { VehicleResponseProps } from '../../../../types/VehicleResponse';
import VinSearchInput from './VinSearchInput';
import { IconCustomLink } from 'wg-fe-ui';
import useTracking from '../hooks/tracking/useTracking';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  error?: string;
  onChange: (vehicle: VehicleResponseProps[], vin: string) => void;
  onError?: () => void;
  isLoading: boolean;
  isFreemium?: boolean;
  hasError?: string;
  setIsLoading: (p: boolean) => void;
  isExistingCar?: boolean;
  onNotFoundClick?: (vin?: string, res?: string) => void;
  disabled?: boolean;
  initialValue?: string;
}

const VinLookup: React.FC<React.PropsWithChildren<Props>> = ({
  error,
  onChange,
  isLoading,
  hasError,
  setIsLoading,
  onError,
  onNotFoundClick,
  disabled = false,
  initialValue,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [lookupError, setLookupError] = useState<string>();
  const [lookupWarning, setLookupWarning] = useState<string>();
  const [searchedVin, setSearchedVin] = useState<string>();
  const { trackEvent } = useTracking();

  useEffect(() => {
    if (initialValue) handleSearch(initialValue);
  }, []);

  const handleSearch = async (vin?: string) => {
    trackEvent('Lookup In Toolkit Car Search Vin');
    setSearchedVin(vin);
    if (disabled) return;
    if (!vin?.match(/^([0-9a-hj-npr-zA-HJ-NPR-Z]{17})+$/))
      return setLookupWarning(t('Not a valid vin'));
    if (vin?.length !== 17)
      return setLookupWarning(t('A vin is 17 characters long'));
    setLookupWarning(undefined);
    setIsLoading(true);
    const [resp, status] = await retrieveVehiclesByVin(vin?.trim());
    if (status !== 200 || resp?.code > 201) {
      setIsLoading(false);
      setLookupError(resp.msg);
      onError && onError();
      return;
    }
    setLookupError(undefined);
    setIsLoading(false);
    onChange(resp?.items, vin);
  };

  useEffect(() => {
    if (hasError) setLookupError(hasError);
  }, [hasError]);

  return (
    <ThemeConsumer>
      {(theme) => (
        <>
          <VinSearchInput
            warning={lookupWarning}
            onSearch={handleSearch}
            disabled={disabled}
            isLoading={isLoading}
            initialValue={initialValue}
            {...otherProps}
          />
          {lookupError && (
            <ErrorContainer>
              <img
                src="https://files.wegroup.be/images/IllustrationVehicleNotFound.svg"
                alt="loop illustration"
              />
              <h3>{lookupError}</h3>
              <p>{t('VinNotFoundText')}</p>
              {onNotFoundClick && !isSocialLoginUrl && (
                <span onClick={() => onNotFoundClick(searchedVin, lookupError)}>
                  {t('Should the VIN have been found? Let us know!')}
                  <IconCustomLink color={theme.labels.guaranteeText} />{' '}
                </span>
              )}
            </ErrorContainer>
          )}
        </>
      )}
    </ThemeConsumer>
  );
};

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 55.2rem;
  margin: 0 auto;

  > h3 {
    font-size: 2.8rem;
    font-weight: 700;
    margin-bottom: 1.6rem;
    line-height: 1.1;
  }

  > p,
  span {
    color: ${({ theme }) => theme.labels.guaranteeText};

    > svg {
      width: 1.2rem;
      height: 1.2rem;
      margin-left: 0.4rem;
    }
  }

  span {
    margin-top: 1.6rem;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: ${({ theme }) => theme.brand.primary};
    }
  }
`;

export default VinLookup;
